import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RequestService } from '@foundation/network/services';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';

interface AssistantToolInfo {
	name: string;
	description: string;
	shortDescription: string | null;
	inputSchema: Record<string, unknown> | null;
	source: string;
}

interface AssistantToolsResult extends Record<string, unknown> {
	tools: AssistantToolInfo[];
}

@Component({
	selector: 'lib-admin-assistant-tools-page',
	standalone: true,
	imports: [CommonModule, FormsModule, TranslateDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="flex h-full min-h-0 flex-col gap-6">
			<div class="shrink-0">
				<h1 class="text-3xl font-bold" [translate]>Assistant Tools</h1>
				<p class="text-base-content/60 mt-2" [translate]>
					Tools available to the assistant background task via the configured MCP server.
				</p>
			</div>

			@if (isLoading()) {
				<div class="flex items-center gap-3">
					<span class="loading loading-spinner loading-sm"></span>
					<span class="text-base-content/60 text-sm" [translate]>Loading tools…</span>
				</div>
			} @else if (loadError()) {
				<div
					role="alert"
					class="alert alert-error"
				>
					<span>{{ loadError() }}</span>
				</div>
			} @else if (tools().length === 0) {
				<div
					role="alert"
					class="alert alert-warning"
				>
					<span [translate]>No tools available. Make sure the MCP server URL is configured (ASSISTANT_MCP_SERVER_URL).</span>
				</div>
			} @else {
				<div class="flex shrink-0 items-center gap-3">
					<input
						type="text"
						[ngModel]="query()"
						(ngModelChange)="query.set($event)"
						placeholder="Filter by name, description or source…"
						class="input input-bordered input-sm flex-1"
					/>
					<p class="text-base-content/60 shrink-0 text-sm">
						{{ filteredTools().length }} / {{ tools().length }} <span [translate]>tool(s)</span>
					</p>
				</div>

				<div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
					@for (tool of filteredTools(); track tool.name) {
						<div class="bg-base-100 border-base-200 shrink-0 rounded-xl border p-5 shadow-sm">
							<div class="flex items-start justify-between gap-2">
								<code class="text-primary font-mono text-sm font-semibold">{{ tool.name }}</code>
								<span class="badge badge-ghost badge-sm shrink-0">{{ tool.source }}</span>
							</div>
							<p class="text-base-content/80 mt-2 text-sm">
								{{ tool.shortDescription ?? tool.description }}
							</p>
						</div>
					} @empty {
						<div role="alert" class="alert">
							<span [translate]>No tools match the current filter.</span>
						</div>
					}
				</div>
			}
		</div>
	`,
})
export class AdminAssistantToolsPageComponent implements OnInit {
	private _requestService = inject(RequestService);
	private _translationService = inject(TranslationService);

	isLoading = signal(true);
	tools = signal<AssistantToolInfo[]>([]);
	loadError = signal<string | null>(null);
	query = signal<string>('');

	filteredTools = computed(() => {
		const needle = this.query().trim().toLowerCase();
		if (!needle) return this.tools();
		return this.tools().filter((tool) => {
			const haystack = [tool.name, tool.shortDescription ?? '', tool.description ?? '', tool.source]
				.join(' ')
				.toLowerCase();
			return haystack.includes(needle);
		});
	});

	private _i18n_loadError = this._translationService.prep('Failed to load assistant tools.');

	ngOnInit(): void {
		this._requestService.getBasic$<AssistantToolsResult>('/api/assistants/tools').subscribe({
			next: (response) => {
				this.isLoading.set(false);
				if (response.error) {
					this.loadError.set(response.error.title);
					return;
				}
				this.tools.set(response.result?.tools ?? []);
			},
			error: () => {
				this.isLoading.set(false);
				this.loadError.set(this._i18n_loadError());
			},
		});
	}
}
